import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Textarea } from "@/shared/ui/textarea";
import { Badge } from "@/shared/ui/badge";
import { Plus, Trash2, Trophy, Lightbulb, Star, Users, CheckCircle, Send } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useFactory } from "@/core/hooks/useFactory";
import { getClaimStatus, updateClaimStatus } from "../api/claims";
import { fetchRootCauseByMethod, upsertRootCauseForClaim } from "../api/root-causes";
import { fetchTeamMembersByClaim } from "../api/team-members";
import { createSupplierPortalClient } from "../api/supplier-portal";
import { queryKeys } from "@/core/lib/queryKeys";
import { toast } from "sonner";
import type { Json } from "@/integrations/supabase/types";

interface TeamRecognitionProps {
  claimId: string;
  isSupplierView?: boolean;
  supplierToken?: string;
}

interface LessonLearned {
  id: string;
  category: "success" | "improvement" | "knowledge";
  description: string;
}

interface RecognitionData {
  lessonsLearned: LessonLearned[];
  teamAcknowledgement: string;
  closureSummary: string;
}

const lessonCategories = {
  success: { icon: Trophy, label: "What Worked Well", color: "bg-success/20 text-success" },
  improvement: { icon: Lightbulb, label: "Area for Improvement", color: "bg-warning/20 text-warning" },
  knowledge: { icon: Star, label: "Key Learning", color: "bg-chart-2/20 text-chart-2" },
};

export function TeamRecognition({ claimId, isSupplierView = false, supplierToken }: TeamRecognitionProps) {
  const queryClient = useQueryClient();
  const { selectedFactory } = useFactory();
  const [newLesson, setNewLesson] = useState<Partial<LessonLearned>>({
    category: "success",
    description: "",
  });
  const [acknowledgementDraft, setAcknowledgementDraft] = useState("");
  const [closureDraft, setClosureDraft] = useState("");
  const supplierClient = supplierToken ? createSupplierPortalClient(supplierToken) : null;

  const { data: claimData } = useQuery({
    queryKey: ["mod_8d_claim_status", claimId],
    queryFn: () => supplierClient?.fetchClaim().then((claim) => claim.status) ?? getClaimStatus(claimId),
  });

  const { data: recognitionData, isLoading } = useQuery({
    queryKey: ["mod_8d_recognition", claimId],
    queryFn: async () => {
      if (supplierClient) {
        const data = await supplierClient.fetchRootCauses("recognition");
        const rootCause = Array.isArray(data) ? data[0] : data;
        return (rootCause?.data as unknown as RecognitionData) || { lessonsLearned: [], teamAcknowledgement: "", closureSummary: "" };
      }
      const rootCause = await fetchRootCauseByMethod(claimId, "recognition");
      return (rootCause?.data as unknown as RecognitionData) || { lessonsLearned: [], teamAcknowledgement: "", closureSummary: "" };
    },
  });

  const { data: teamMembers } = useQuery({
    queryKey: ["mod_8d_team_members", claimId],
    queryFn: () => supplierClient?.fetchTeamMembers() ?? fetchTeamMembersByClaim(claimId),
  });

  const saveMutation = useMutation({
    mutationFn: async (newData: RecognitionData) => {
      if (supplierClient) {
        await supplierClient.upsertRootCause("recognition", newData as unknown as Json);
        return;
      }
      await upsertRootCauseForClaim(claimId, "recognition", newData as unknown as Json);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mod_8d_recognition", claimId] });
      toast.success("Recognition data saved");
    },
    onError: () => {
      toast.error("Failed to save recognition data");
    },
  });

  useEffect(() => {
    if (!recognitionData) return;
    setAcknowledgementDraft(recognitionData.teamAcknowledgement || "");
    setClosureDraft(recognitionData.closureSummary || "");
  }, [recognitionData?.teamAcknowledgement, recognitionData?.closureSummary]);

  const submitForApprovalMutation = useMutation({
    mutationFn: async () => {
      if (supplierClient) {
        await supplierClient.updateClaimStatus("pending_approval");
        return;
      }
      await updateClaimStatus(claimId, "pending_approval");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mod_8d_claim_status", claimId] });
      if (!supplierClient) {
        queryClient.invalidateQueries({ queryKey: queryKeys.mod8dClaims(selectedFactory?.id ?? null) });
      }
      toast.success("8D submitted for customer approval");
    },
    onError: () => {
      toast.error("Failed to submit for approval");
    },
  });

  const addLesson = () => {
    if (!newLesson.description?.trim()) return;

    const lesson: LessonLearned = {
      id: crypto.randomUUID(),
      category: newLesson.category as LessonLearned["category"],
      description: newLesson.description,
    };

    const updated: RecognitionData = {
      lessonsLearned: [...(recognitionData?.lessonsLearned || []), lesson],
      teamAcknowledgement: recognitionData?.teamAcknowledgement || "",
      closureSummary: recognitionData?.closureSummary || "",
    };

    saveMutation.mutate(updated);
    setNewLesson({ category: "success", description: "" });
  };

  const deleteLesson = (id: string) => {
    if (!recognitionData) return;

    const updated: RecognitionData = {
      ...recognitionData,
      lessonsLearned: recognitionData.lessonsLearned.filter((l) => l.id !== id),
    };

    saveMutation.mutate(updated);
  };

  const updateField = (field: keyof RecognitionData, value: string) => {
    const updated: RecognitionData = {
      lessonsLearned: recognitionData?.lessonsLearned || [],
      teamAcknowledgement: acknowledgementDraft,
      closureSummary: closureDraft,
      [field]: value,
    };

    saveMutation.mutate(updated);
  };

  const acknowledgementDirty = acknowledgementDraft !== (recognitionData?.teamAcknowledgement || "");
  const closureDirty = closureDraft !== (recognitionData?.closureSummary || "");

  const saveAcknowledgement = () => {
    updateField("teamAcknowledgement", acknowledgementDraft);
  };

  const saveClosureSummary = () => {
    updateField("closureSummary", closureDraft);
  };

  if (isLoading) {
    return <div className="animate-pulse h-32 bg-muted rounded-lg" />;
  }

  return (
    <div className="space-y-4">
      {/* Team Recognition */}
      <Card className="border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4 text-primary" />
            Team Recognition
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {teamMembers && teamMembers.length > 0 && (
            <div className="flex flex-wrap gap-2 p-3 bg-muted/50 rounded-lg">
              {teamMembers.map((member) => (
                <Badge key={member.id} variant="secondary" className="text-sm">
                  <Trophy className="h-3 w-3 mr-1 text-warning" />
                  {member.name}
                  <span className="ml-1 text-muted-foreground">({member.role})</span>
                </Badge>
              ))}
            </div>
          )}
          <Textarea
            placeholder="Acknowledge team contributions and individual efforts..."
            value={acknowledgementDraft}
            onChange={(e) => setAcknowledgementDraft(e.target.value)}
            className="min-h-[80px]"
          />
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={saveAcknowledgement}
              disabled={!acknowledgementDirty || saveMutation.isPending}
            >
              {saveMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lessons Learned */}
      <Card className="border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Lightbulb className="h-4 w-4 text-primary" />
            Lessons Learned
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add new lesson */}
          <div className="flex gap-2">
            <select
              value={newLesson.category}
              onChange={(e) => setNewLesson({ ...newLesson, category: e.target.value as LessonLearned["category"] })}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm w-44"
            >
              <option value="success">What Worked Well</option>
              <option value="improvement">Area for Improvement</option>
              <option value="knowledge">Key Learning</option>
            </select>
            <Input
              placeholder="Describe the lesson learned..."
              value={newLesson.description}
              onChange={(e) => setNewLesson({ ...newLesson, description: e.target.value })}
              className="flex-1"
            />
            <Button size="icon" onClick={addLesson} disabled={!newLesson.description?.trim()}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          {/* Lessons list */}
          <div className="space-y-2">
            {recognitionData?.lessonsLearned.map((lesson) => {
              const config = lessonCategories[lesson.category];
              const Icon = config.icon;

              return (
                <div
                  key={lesson.id}
                  className="flex items-center gap-3 p-3 rounded-lg border border-border bg-background"
                >
                  <Badge className={`${config.color} shrink-0`}>
                    <Icon className="h-3 w-3 mr-1" />
                    {config.label}
                  </Badge>
                  <span className="flex-1 text-sm">{lesson.description}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => deleteLesson(lesson.id)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              );
            })}
            {(!recognitionData?.lessonsLearned || recognitionData.lessonsLearned.length === 0) && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No lessons learned added yet
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Closure Summary */}
      <Card className="border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Star className="h-4 w-4 text-primary" />
            Closure Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            placeholder="Summarize the 8D process outcome, key decisions made, and final resolution status..."
            value={closureDraft}
            onChange={(e) => setClosureDraft(e.target.value)}
            className="min-h-[100px]"
          />
          <div className="mt-3 flex justify-end">
            <Button
              size="sm"
              onClick={saveClosureSummary}
              disabled={!closureDirty || saveMutation.isPending}
            >
              {saveMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Submit for Approval - Supplier View Only */}
      {isSupplierView && claimData !== "pending_approval" && claimData !== "closed" && (
        <Card className="border-primary/50 bg-primary/5">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="p-3 rounded-full bg-primary/20">
                <Send className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Complete 8D Process</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Once you have completed all steps and documented the lessons learned, submit this 8D for customer approval.
                </p>
              </div>
              <Button 
                onClick={() => submitForApprovalMutation.mutate()}
                disabled={submitForApprovalMutation.isPending}
                className="gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                {submitForApprovalMutation.isPending ? "Submitting..." : "Submit for Customer Approval"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {claimData === "pending_approval" && (
        <Card className="border-warning/50 bg-warning/10">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 text-center justify-center">
              <CheckCircle className="h-5 w-5 text-warning" />
              <span className="font-medium text-warning">Awaiting Customer Approval</span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
